import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { SVGIconType } from '../../../constants/svgIcon';
import { SvgIcon } from '../SVGIcon';

import styles from './TTSPlayer.module.css';

interface TTSPlayerProps {
  audioSrc: string;
  onClickPlay?: () => void;
}

export interface TTSPlayerRef {
  stop: () => void;
}

export const TTSPlayer = forwardRef(
  ({ audioSrc, onClickPlay }: TTSPlayerProps, ref) => {
    const [status, setStatus] = useState<'play' | 'stop'>('stop');
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const togglePlay = () => {
      onClickPlay?.();
      if (audioRef.current) {
        if (status === 'stop') {
          void audioRef.current.play();
          setStatus('play');
        } else {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setStatus('stop');
        }
      }
    };

    const getIcon = () => {
      return status === 'play' ? (
        <SvgIcon name={SVGIconType.PlayerStop} width={20} height={20} />
      ) : (
        <SvgIcon name={SVGIconType.PlayerPlay} width={20} height={20} />
      );
    };

    const handleAudioEnded = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0; // Reset to the beginning of the audio
      }
      setStatus('stop');
    };

    useImperativeHandle(
      ref,
      () => ({
        stop: handleAudioEnded,
      }),
      [],
    );

    return (
      <div className={styles.ttsPlayer}>
        <div onClick={togglePlay} className={styles.playButton}>
          {getIcon()}
        </div>
        <div className={styles.indicatorContainer}>
          {status === 'play' && (
            <div className={`${styles.indicator} ${styles.playing}`} />
          )}
        </div>
        <audio ref={audioRef} src={audioSrc} onEnded={handleAudioEnded} />
      </div>
    );
  },
);
